import React from 'react';
import { PageHeader, PageFooter } from './PageDecoration';

const StrengthsPagePart2 = ({ data, calculations, name, pageNum = 6 }) => {
    const isCouple = data?.personalInfo?.familyType === 'couple';
    const metrics = calculations?.metrics || {};
    
    const safeParse = (v) => { 
        if (typeof v === "number") return v; 
        if (!v) return 0; 
        return parseFloat(v.toString().replace(/[₹, \s]/g, "")) || 0; 
    };

    if (isCouple) {
        const jointSalary = data?.jointSalary || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};

        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const hTotal = hSalary + hOther;
        const wTotal = wSalary + wOther;
        const combinedMonthly = hTotal + wTotal;

        const isDual = hTotal > 0 && wTotal > 0;
        const dining = safeParse(sectionC.dining) || 0;
        const shopping = safeParse(sectionC.shopping) || 0;
        const lifestyleSpending = dining + shopping;
        const lsPct = combinedMonthly > 0 ? (lifestyleSpending / combinedMonthly) * 100 : 0;

        const homeLoanEMI = safeParse(sectionC.homeLoanEMI) || safeParse(sectionF.homeLoanEMI) || 0;
        const homeLoanOutstanding = safeParse(sectionF.homeLoanOutstanding) || 0;
        const hasHomeLoan = homeLoanEMI > 0;

        let totalHomeDeduction = 0;
        if (hasHomeLoan) {
            const principalRatio = homeLoanOutstanding > 0 ? Math.min(0.55, Math.max(0.35, (homeLoanEMI * 12) / (homeLoanOutstanding + homeLoanEMI * 120))) : 0.5;
            const annualPrincipal = homeLoanEMI * 12 * principalRatio;
            const section24Interest = Math.min(400000, homeLoanEMI * 12 * (1 - principalRatio));
            const section80CPrincipal = Math.min(300000, annualPrincipal);
            totalHomeDeduction = section24Interest + section80CPrincipal;
        }

        const ccOS = safeParse(sectionF.creditCardOutstanding) || 0;
        const ccEMI = safeParse(sectionC.creditCardEMI) || safeParse(sectionF.creditCardEMI) || 0;
        const hasNoCCDebt = ccOS === 0 && ccEMI === 0;

        const hHealthCover = safeParse(sectionE.husbandHealthCover) || 0;
        const wHealthCover = safeParse(sectionE.wifeHealthCover) || 0;
        const combinedCover = hHealthCover + wHealthCover;

        const s1Title = isDual ? "Dual Income Stability" : "Single Income Focus";
        const s1Body = isDual 
            ? `Two earners mean one job loss does not break you. Both of you are actively contributing to the household income (Husband: ₹${hTotal.toLocaleString('en-IN')}/mo, Wife: ₹${wTotal.toLocaleString('en-IN')}/mo), providing a highly resilient financial safety net.`
            : `A steady income source of ₹${(hTotal + wTotal).toLocaleString('en-IN')}/month is the foundation of your plan. Focusing on career growth and building a robust emergency fund is key to backing up this single engine.`;

        const s2Title = lsPct <= 20 ? `Low Lifestyle Spending (${Math.round(lsPct)}%)` : `Lifestyle Spending Control (${Math.round(lsPct)}%)`;
        const s2Body = lsPct <= 20
            ? `You enjoy life without overspending. Keeping combined lifestyle spending at ${lsPct.toFixed(1)}% of your income shows exceptional financial discipline, allowing you to route more towards wealth creation.`
            : `Your combined lifestyle spending is ${lsPct.toFixed(1)}% of income. Aligning this closer to the 10-15% benchmark will free up significant capital for your long-term goals.`;

        const s3Title = hasHomeLoan ? "Home Loan as Wealth Tool" : "Zero Home Loan Liabilities";
        const s3Body = hasHomeLoan
            ? `₹${(homeLoanEMI/1000).toFixed(0)}k EMI builds equity + joint tax deduction (₹${(totalHomeDeduction/100000).toFixed(1)}L/year combined). Your home loan EMI is not just an expense; it is forced savings building real estate equity.`
            : `You enjoy the ultimate debt freedom with zero home loan liabilities. This gives you a clean slate and massive capacity to redirect your monthly surplus directly into high-growth wealth creators.`;

        const s4Title = hasNoCCDebt ? "No Credit Card Debt" : "Credit Card Action Plan";
        const s4Body = hasNoCCDebt
            ? "You carry no high-interest revolving debt. Excellent. By keeping your short-term liabilities at zero, you avoid paying 36-42% interest rates that destroy wealth."
            : `You have ₹${ccOS.toLocaleString('en-IN')} outstanding card dues. Resolving this high-interest debt quickly is a high priority to protect your monthly savings.`;

        const s5Title = combinedCover > 0 ? "Employer Health Insurance" : "Family Health Protection";
        const s5Body = combinedCover > 0
            ? `₹${(combinedCover/100000).toFixed(0)}L combined base cover (needs family floater top-up). Having employer-sponsored health insurance protects your hard-earned savings from unexpected medical emergencies.`
            : "Active health insurance plan is running to shield family savings from unexpected hospitalisation costs. Securing a base cover is a smart protective decision.";

        const cards = [
            { num: 1, title: s1Title, body: s1Body, icon: "🪙" },
            { num: 2, title: s2Title, body: s2Body, icon: "🐷" },
            { num: 3, title: s3Title, body: s3Body, icon: "🏠" },
            { num: 4, title: s4Title, body: s4Body, icon: "💳" },
            { num: 5, title: s5Title, body: s5Body, icon: "🛡️" }
        ];

        return (
            <div className="pdf-page strengths-page-2" style={{ height: '297mm', width: '210mm', position: 'relative', background: '#fff', padding: '120px 80px 80px' }}>
                <PageHeader sectionNumber={3} />

                <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#059669', marginBottom: '20px', textTransform: 'uppercase' }}>WHAT IS WORKING WELL FOR YOU</h2>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {cards.map((c, idx) => (
                        <div key={idx} style={{ background: '#f8fafc', padding: '12px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <div style={{ background: '#D1FAE5', width: '34px', height: '34px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>{c.icon}</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '800', fontSize: '15px', color: '#111827', marginBottom: '2px' }}>{c.num}. {c.title}</div>
                                <div style={{ fontSize: '12.5px', color: '#1e293b', lineHeight: '1.4', fontWeight: '500' }}>{c.body}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <PageFooter pageNumber={pageNum} clientName={calculations.name} />
            </div>
        );
    }

    const homeLoanEMI = metrics.homeLoanEMI || 18000;
    const principal = Math.round(homeLoanEMI * 0.55);

    return (
        <div className="pdf-page strengths-page-2" style={{ height: '297mm', width: '210mm', position: 'relative', background: '#fff', padding: '120px 80px 80px' }}>
            <PageHeader sectionNumber={3} />

            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#059669', marginBottom: '30px', textTransform: 'uppercase' }}>STRENGTH 3: Home Loan Building Dual Wealth</h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '40px' }}>
                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '24px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ background: '#1e293b', color: '#fff', width: '40px', height: '40px', borderRadius: '50%', margin: '0 auto 15px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🏠</div>
                    <div style={{ fontWeight: '800', fontSize: '16px', color: '#1e293b', marginBottom: '8px' }}>Wealth Machine</div>
                    <p style={{ fontSize: '11px', color: '#1e293b', lineHeight: '1.5' }}>Your home loan is your most powerful wealth-building tool, building equity in real estate asset.</p>
                </div>
                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '24px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ background: '#1e293b', color: '#fff', width: '40px', height: '40px', borderRadius: '50%', margin: '0 auto 15px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>📊</div>
                    <div style={{ fontWeight: '800', fontSize: '16px', color: '#1e293b', marginBottom: '8px' }}>The numbers:</div>
                    <p style={{ fontSize: '11px', color: '#1e293b', lineHeight: '1.5' }}>₹{principal.toLocaleString()} of your EMI currently goes toward principal repayment and home equity.</p>
                </div>
                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '24px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ background: '#1e293b', color: '#fff', width: '40px', height: '40px', borderRadius: '50%', margin: '0 auto 15px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🛡️</div>
                    <div style={{ fontWeight: '800', fontSize: '16px', color: '#1e293b', marginBottom: '8px' }}>Tax benefit:</div>
                    <p style={{ fontSize: '11px', color: '#1e293b', lineHeight: '1.5' }}>You can claim combined ₹3,20,000 tax deduction every year from your home loan.</p>
                </div>
            </div>

            <div style={{ background: '#059669', padding: '30px', borderRadius: '24px', color: '#fff', marginBottom: '40px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '15px', textTransform: 'uppercase' }}>STRENGTH 4: Smart 80C Strategy</h3>
                <p style={{ fontSize: '13px', opacity: '0.9', lineHeight: '1.6', marginBottom: '20px' }}>
                    Your combined 80C utilization is ₹1,44,000 — 96% of the limit. You have built a multi-instrument
                    tax strategy that protects you and saves you thousands per year versus the average taxpayer.
                </p>
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '20px', fontSize: '13px', fontWeight: '800' }}>
                    Bonus fact: Never leave a job without ensuring your EPF is transferred.
                </div>
            </div>

            <div style={{ padding: '0 20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#111827', marginBottom: '20px', textTransform: 'uppercase' }}>STRENGTH 5: Investing Habit Running</h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                    <li style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                        <span style={{ color: '#059669', fontSize: '20px' }}>•</span>
                        <div style={{ fontSize: '13px', color: '#1e293b', lineHeight: '1.5' }}>You have established the two most important habits: a mutual fund SIP and an emergency fund saving.</div>
                    </li>
                    <li style={{ display: 'flex', gap: '15px' }}>
                        <span style={{ color: '#059669', fontSize: '20px' }}>•</span>
                        <div style={{ fontSize: '13px', color: '#1e293b', lineHeight: '1.5' }}>Your ₹3,000/month SIP, continued for 28 years, will grow to approximately ₹1.08 Crore. Doubling it to ₹6,000 makes that ₹2.16 Crore.</div>
                    </li>
                </ul>
            </div>

            <PageFooter pageNumber={pageNum} clientName={calculations.name} />
        </div>
    );
};

export const JointSavingsCompoundingBlueprint = ({ data, calculations, name, pageNum = 7 }) => {
    const metrics = calculations?.metrics || {};
    const isCouple = data?.personalInfo?.familyType === 'couple';
    
    const safeParse = (v) => { 
        if (typeof v === "number") return v; 
        if (!v) return 0; 
        return parseFloat(v.toString().replace(/[₹, \s]/g, "")) || 0; 
    };

    if (!isCouple) return null;

    const jointSalary = data?.jointSalary || {};
    const sectionB = jointSalary.sectionB || {};

    const hSalary = safeParse(sectionB.husbandSalary) || 0;
    const wSalary = safeParse(sectionB.wifeSalary) || 0;
    const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
    const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
    const combinedMonthly = hSalary + hOther + wSalary + wOther;

    const surplus = safeParse(metrics?.monthlySurplus) || 0;
    const monthlySavings = surplus > 0 ? surplus : (combinedMonthly * 0.15 > 0 ? Math.round(combinedMonthly * 0.15 / 1000) * 1000 : 20000);

    const introTxt = `If you automate your estimated joint monthly surplus of ₹${monthlySavings.toLocaleString('en-IN')}/month, here is how compounding will work for you at a conservative 12% average annual return:`;

    const years = [15, 20, 25];
    const rRate = 0.01; // 1% monthly

    const fmtAmt = (val) => {
        if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
        return `₹${(val / 100000).toFixed(2)} Lakhs`;
    };

    return (
        <div className="pdf-page compounding-blueprint-page" style={{ height: '297mm', width: '210mm', position: 'relative', background: '#fff', padding: '120px 80px 80px', boxSizing: 'border-box' }}>
            <PageHeader sectionNumber={6} />

            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#059669', marginBottom: '20px', textTransform: 'uppercase' }}>JOINT SAVINGS COMPOUNDING BLUEPRINT</h2>
            
            <p style={{ fontSize: '14px', color: '#111827', lineHeight: '1.6', marginBottom: '30px', fontWeight: '500' }}>{introTxt}</p>

            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', marginBottom: '15px' }}>
                <div style={{ display: 'flex', background: '#059669', color: '#fff', padding: '16px 20px', fontSize: '15px', fontWeight: '800' }}>
                    <div style={{ width: '25%' }}>Years</div>
                    <div style={{ width: '35%', textAlign: 'center' }}>Total Invested</div>
                    <div style={{ width: '40%', textAlign: 'right' }}>Estimated Wealth (12% Return)</div>
                </div>
                {years.map((t, rIdx) => {
                    const totalInvested = t * 12 * monthlySavings;
                    const fv = monthlySavings * ((Math.pow(1 + rRate, t * 12) - 1) / rRate) * (1 + rRate);
                    return (
                        <div key={rIdx} style={{ display: 'flex', padding: '16px 20px', borderBottom: rIdx < 2 ? '1px solid #e2e8f0' : 'none', background: rIdx % 2 === 0 ? '#fff' : '#f8fafc', fontSize: '15px', alignItems: 'center' }}>
                            <div style={{ width: '25%', fontWeight: '700', color: '#111827' }}>{t} Years</div>
                            <div style={{ width: '35%', textAlign: 'center', color: '#1e293b', fontWeight: '500' }}>{fmtAmt(totalInvested)}</div>
                            <div style={{ width: '40%', textAlign: 'right', fontWeight: '850', color: '#059669' }}>{fmtAmt(fv)}</div>
                        </div>
                    );
                })}
            </div>

            <p style={{ fontSize: '11.5px', color: '#1e293b', lineHeight: '1.5', marginBottom: '35px', fontWeight: '500' }}>
                Note: Values calculated assuming monthly compounding at a constant 12% return rate. Actual returns will vary depending on your dynamic asset allocation choice.
            </p>

            <div style={{ background: '#f0fdfa', border: '1px solid #ccfbf1', padding: '24px', borderRadius: '16px', marginBottom: '40px' }}>
                <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#0f766e', marginBottom: '10px', marginTop: 0, textTransform: 'uppercase' }}>THE POWER OF JOINT WEALTH CREATION</h3>
                <p style={{ fontSize: '13.5px', color: '#1e293b', lineHeight: '1.6', margin: 0, fontWeight: '500' }}>
                    By planning together as a couple, you pool your resources, tax benefits, and borrowing power. This dual engine is the fastest way to compound wealth in India. Focus on closing high-interest gaps first, then automate your monthly surplus into long-term equity to secure your joint financial independence.
                </p>
            </div>

            <PageFooter pageNumber={pageNum} clientName={calculations.name} />
        </div>
    );
};

export default StrengthsPagePart2;
